import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import axios from "axios";
import * as rdd from "react-device-detect";
import { url_55 } from "../../../custom_utilities/api_services";
import userActivityActions from "../../../redux/user_activity/action";
import { setLiveVideoStreamDetails } from "../../../redux/live_video_streaming/LiveVideoStreamingSlice";
import { setHomeworkTestDetails } from "../../../redux/homework/HomeWorkSlice";
import { getLiveClassStatus } from "../utils/dailyPathUtils";

const decryptZoomId = (val) => {
  const CryptoJS = require("crypto-js");
  let encrypted = val;
  const key = "0CiuKpLCZbX7gBCyYTka4USxTnh7ssXKk8bJ6Y3TXPc=";
  encrypted = atob(encrypted);
  encrypted = JSON.parse(encrypted);
  const iv = CryptoJS.enc.Base64.parse(encrypted.iv);
  const value = encrypted.value;
  const parsedKey = CryptoJS.enc.Base64.parse(key);
  const decrypted = CryptoJS.AES.decrypt(value, parsedKey, { iv });
  return decrypted.toString(CryptoJS.enc.Utf8).split("*")[1];
};

export const useDailyPathNavigation = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const isPaid = useSelector((state) => state.auth?.user?.is_paid ?? state.auth?.userData?.is_paid);
  const token = useSelector((state) => state.auth.authToken);
  const tokenType = useSelector((state) => state.auth.tokenType);

  const requirePaidAccess = useCallback(() => {
    if (Number(isPaid) === 0) {
      alert("You haven't purchased a plan. Please purchase it from store.");
      history.push("/store");
      return false;
    }
    return true;
  }, [history, isPaid]);

  const openRecordedClass = useCallback(
    (entry) => {
      if (!requirePaidAccess()) return;
      const recordedId = entry?.rec_id ?? entry?.recId ?? entry?.id ?? entry?.zoom_id;
      if (!recordedId) return;
      dispatch(userActivityActions.setRecordedVideoData(entry));
      history.push("/recorded");
    },
    [dispatch, history, requirePaidAccess]
  );

  const openLiveStream = useCallback(
    async (entry) => {
      if (!requirePaidAccess()) return;
      if (getLiveClassStatus(entry) === "ended") return;

      if (String(entry.stream_service) === "3") {
        const joinUrl = entry?.student_join_url ?? entry?.join_url;
        if (joinUrl) window.location.assign(joinUrl);
        return;
      }

      const zoomId = decryptZoomId(entry.zoom_id);
      const data = {
        device_id: rdd.fullBrowserVersion,
        device_type: rdd.browserName,
        device_name: rdd.osName,
        zoom_id: zoomId,
        type: "Web",
      };

      try {
        const response = await axios.post(url_55, data, {
          headers: token && tokenType ? { Authorization: `${tokenType} ${token}` } : {},
        });

        if (response.data.status !== 200) {
          alert(response.data.message || "Unable to join live class.");
          return;
        }

        const streamPayload = {
          ...response.data.data,
          ...entry,
          redirectedFrom: "study_dashboard",
        };

        if (String(entry.stream_service) === "1") {
          streamPayload.redirectedFrom = "study_dashboard";
          dispatch(userActivityActions.setLiveStreamData(streamPayload));
          history.push("/zoom");
          return;
        }

        if (String(entry.stream_service) === "2") {
          dispatch(
            setLiveVideoStreamDetails({
              zoomId,
              isLive: true,
              live_class_id: entry.id,
              normalVideos: false,
            })
          );
          history.push({
            pathname: "/streaming",
            state: { zoomId, isLive: true, live_class_id: entry.id },
          });
          return;
        }

        alert("There was an error, Please try again later");
      } catch {
        alert("Unable to join live class right now.");
      }
    },
    [dispatch, history, requirePaidAccess, token, tokenType]
  );

  const handleDailyPathItemClick = useCallback(
    (item) => {
      const entry = item?.payload ?? item;
      if (!entry) return;

      switch (item?.type) {
        case "live_class": {
          const status = getLiveClassStatus(entry);
          if (status === "recorded") {
            openRecordedClass(entry);
            return;
          }
          if (status === "ended") return;
          openLiveStream(entry);
          return;
        }
        case "recorded_class":
          openRecordedClass(entry);
          return;
        case "video": {
          if (entry?.is_paid) {
            alert("You haven't purchased a plan. Please purchase it from store.");
            history.push("/store");
            return;
          }
          const videoData = { ...entry, redirectedFrom: "study_dashboard" };
          dispatch(userActivityActions.setVideoData(videoData));
          history.push("/videos");
          return;
        }
        case "notes": {
          if (!entry?.is_paid) {
            alert("You haven't purchased a plan. Please purchase it from store.");
            history.push("/store");
            return;
          }
          const notesData = { ...entry, redirectedFrom: "study_dashboard" };
          dispatch(userActivityActions.setNotesData(notesData));
          history.push("/notes");
          return;
        }
        case "homework": {
          if (entry?.status === "Pending") {
            history.push("/homework-test");
          } else {
            history.push("/homework-solution");
          }
          dispatch(setHomeworkTestDetails(entry));
          return;
        }
        default:
          return;
      }
    },
    [dispatch, history, isPaid, openLiveStream, openRecordedClass]
  );

  return { handleDailyPathItemClick };
};
